import { useState } from "react";
import { Link } from "react-router-dom";

import { formatDateReadable } from "../../api/types";
import type { FxAccount, FxOpeningBalances as FxOpeningBalancesData } from "../../api/types";
import { Badge } from "../../components/Badge";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Field } from "../../components/Field";
import { Money } from "../../components/Money";
import { Table } from "../../components/Table";
import { confirmTitleFor, labelForOption, plainRate, requiresConfirm } from "../fxOpeningActions";
import { pathForStage } from "../stageRoute";
import { t } from "../../i18n/t";

/**
 * FX opening-balance spec §3.7. Presentational, like `EntityToggles`: `ScopeScreen` owns the query and
 * every mutation and hands this component data plus callbacks. Returns `null` (renders nothing) when the
 * source is not FX-capable or has no foreign bank account -- the section simply does not exist for an
 * ordinary GBP-only migration.
 *
 * Round-4 review fix (review-fx-task2-r3.json, item 6): round-3 fixed the Dry-run screen's
 * `FxOpeningChecks` only (confirm-on-BLOCK, per-option labels, the ECB estimate body) -- this findings
 * list still resolved a BLOCK (e.g. the scope-time `fx_opening_balance_sign_mismatch` finding) in one
 * click under a generic "Acknowledge" label, with no ECB body in its `accept_estimate` dialog, and with
 * no gate at all once scope sign-off had frozen the scope (reachable read-only via the stage nav). Now
 * shares `requiresConfirm`/`labelForOption`/`confirmTitleFor` with `FxOpeningChecks` via
 * `../fxOpeningActions`, and every resolve action here is disabled once `!data.editable`, same as the
 * Save/Clear/Accept-estimate buttons in the table above already were.
 */

const STATUS_TONE: Record<FxAccount["status"], "pass" | "warn" | "fail" | "neutral"> = {
  entered: "pass",
  estimate_accepted: "warn",
  missing: "fail",
  not_required: "neutral",
};

const STATUS_LABEL: Record<FxAccount["status"], string> = {
  entered: t("fx.status_entered"),
  estimate_accepted: t("fx.status_estimate_accepted"),
  missing: t("fx.status_missing"),
  not_required: t("fx.status_not_required"),
};

//: A calculated figure is offered only while no real figure is saved for the account.
function showDerived(row: FxAccount): boolean {
  return (
    row.balance === null &&
    row.status !== "estimate_accepted" &&
    row.derived_balance !== null &&
    row.derived_balance !== undefined
  );
}

//: xmg-fx-autofill: the saved figure is one we filled in. Its input stays read-only until Edit is clicked; Save then
//: PUTs it like any typed figure, which makes it the operator's own (the server drops the label).
function autoSourceText(source: string): string | null {
  switch (source) {
    case "xero_transactions":
      return t("fx.autofill_source_xero_transactions");
    case "xero_rate_estimate":
      return t("fx.autofill_source_xero_rate_estimate");
    case "zero_movements":
      return t("fx.autofill_source_zero_movements");
    default:
      return null;
  }
}

//: What the input shows is what Save sends: draft, else saved balance, else the calculated figure.
function currentValue(row: FxAccount, drafts: Record<string, string>): string {
  return drafts[row.account_id] ?? row.balance ?? (showDerived(row) ? row.derived_balance : null) ?? "";
}

//: The calculated-figure hint and "Use this balance" apply only while the input still holds that figure.
function showDerivedUnedited(row: FxAccount, drafts: Record<string, string>): boolean {
  return showDerived(row) && currentValue(row, drafts) === row.derived_balance;
}

//: xmg-fx-opening-scope: the saved figure is implausible against Xero's rate and the input still holds it.
function showRateWarning(row: FxAccount, drafts: Record<string, string>): boolean {
  return (
    row.rate_check?.implausible === true && row.balance !== null && currentValue(row, drafts) === row.balance
  );
}

//: The calculated figure was filled into the input from the warning; it is not saved until Save.
function showSuggestionPending(row: FxAccount, drafts: Record<string, string>): boolean {
  const suggested = row.rate_check?.suggested_balance;
  return (
    suggested !== null &&
    suggested !== undefined &&
    drafts[row.account_id] === suggested &&
    row.balance !== suggested
  );
}

function accountLabel(account: { code: string; name: string }): string {
  return `${account.code} — ${account.name}`;
}

export function FxOpeningBalances({
  data,
  migrationId,
  scopeFrozen,
  saving,
  onSave,
  onClear,
  onAcceptEstimate,
  onRestoreAuto,
  onAcknowledge,
}: {
  data: FxOpeningBalancesData;
  migrationId: string;
  //: Backlog fix (FX opening-balance LOWs, item 4): `!data.editable` is `true` for MANY reasons that are
  //: not "the scope was signed off" -- a migration that never reached `awaiting_scope_signoff` yet, or one
  //: cancelled while an FX BLOCK was still open, is also `!editable`. Whether the scope was genuinely
  //: frozen (§the same `ScopeOut.frozen` -- `migration.scope_sha256 is not None` -- `ScopeScreen` already
  //: computes for its own top banner) must come from THAT signal, not be re-derived from `editable` here.
  scopeFrozen: boolean;
  saving: boolean;
  onSave: (accountId: string, amount: string) => void;
  onClear: (accountId: string) => void;
  onAcceptEstimate: (accountId: string) => void;
  onRestoreAuto: (accountId: string) => void;
  onAcknowledge: (reviewItemId: string, option: string) => void;
}): JSX.Element | null {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  //: Accounts whose filled-in figure the operator opened for editing (xmg-fx-autofill).
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [confirmingAccountId, setConfirmingAccountId] = useState<string | null>(null);
  const [confirmingFinding, setConfirmingFinding] = useState<{
    reviewItemId: string;
    option: string;
    account: string;
    message: string;
  } | null>(null);

  if (!data.supported || data.accounts.length === 0) return null;

  const openFindings = data.accounts.flatMap((account) =>
    account.findings
      // xmg-fx-autofill: the INFO line of a figure we filled in is the label shown on its own row.
      .filter((finding) => finding.resolved_at === null && !finding.key.startsWith("fx_opening_autofilled:"))
      .map((finding) => ({ account, finding })),
  );
  const hasOpenBlock = openFindings.some(({ finding }) => finding.severity === "BLOCK");

  return (
    <section aria-labelledby="scope-fx">
      <h2 id="scope-fx">{t("fx.title")}</h2>
      <p className="muted">{t("fx.caption")}</p>
      {data.blocking > 0 ? <Banner tone="block">{t("fx.blocking", { count: data.blocking })}</Banner> : null}
      <Table<FxAccount>
        caption={t("fx.title")}
        rowKey={(row) => row.account_id}
        rows={data.accounts}
        // §3: a missing balance row takes the fail-row tint, in both themes (ScopeFx.html).
        rowClassName={(row) => (row.status === "missing" ? "is-fail" : undefined)}
        columns={[
          {
            key: "account",
            header: t("fx.col_account"),
            render: (row) => `${row.code} — ${row.name}`,
          },
          { key: "currency", header: t("fx.col_currency"), render: (row) => row.currency },
          {
            key: "base_balance",
            header: t("fx.col_xero_balance", {
              base: data.base_currency,
              as_at: formatDateReadable(data.as_at),
            }),
            render: (row) => <Money value={row.base_balance} currency={data.base_currency} />,
          },
          {
            key: "balance",
            header: t("fx.col_balance"),
            render: (row) =>
              // xmg-fxprefill: nothing to ask for when the Xero base balance is 0 and nothing was entered or
              // accepted -- a plain line instead of an empty input. (A figure entered earlier for such an
              // account has status "entered", so it keeps its input and its Clear button.)
              row.status === "not_required" ? (
                <span>{t("fx.not_required_line", { date: formatDateReadable(data.as_at) })}</span>
              ) : (
                // Round-3 LOW fix (review-fx-task2-r2.json, item 6): the accessible name must include the
                // ACCOUNT, not just the currency -- two same-currency accounts (e.g. an EUR current and an EUR
                // savings account) otherwise get IDENTICAL accessible names, and a screen-reader user
                // tabbing into the second input has no way to tell which account it edits.
                <>
                  <Field
                    label={t("fx.amount_label_for", { currency: row.currency, account: accountLabel(row) })}
                    hint={showDerivedUnedited(row, drafts) ? t("fx.derived_hint") : undefined}
                  >
                    {({ id: fieldId, describedBy }) => (
                      <input
                        id={fieldId}
                        aria-describedby={describedBy}
                        disabled={
                          !data.editable ||
                          saving ||
                          ((row.auto_fill ?? null) !== null && !editing[row.account_id])
                        }
                        value={currentValue(row, drafts)}
                        onChange={(event) => setDrafts({ ...drafts, [row.account_id]: event.target.value })}
                      />
                    )}
                  </Field>
                  {row.auto_fill ? (
                    <div className="fx-autofill" data-level={row.auto_fill.level}>
                      <Badge tone={row.auto_fill.level === "WARN" ? "warn" : "neutral"}>
                        {t("fx.autofill_badge")}
                      </Badge>{" "}
                      <span className="muted">
                        {row.auto_fill.message} {autoSourceText(row.auto_fill.derived_from)}
                      </span>
                    </div>
                  ) : null}
                  {showRateWarning(row, drafts) && row.rate_check ? (
                    <div role="status" className="fx-rate-warning">
                      <p>
                        {t("fx.rate_warning", {
                          implied: plainRate(row.rate_check.implied_rate),
                          currency: row.currency,
                          base: data.base_currency,
                          reference: plainRate(row.rate_check.reference_rate),
                        })}
                      </p>
                      {row.rate_check.suggested_balance ? (
                        <Button
                          aria-label={t("fx.rate_use_suggested_for", {
                            amount: row.rate_check.suggested_balance,
                            currency: row.currency,
                            account: accountLabel(row),
                          })}
                          disabled={!data.editable || saving}
                          onClick={() =>
                            setDrafts((prev) => ({
                              ...prev,
                              [row.account_id]: row.rate_check?.suggested_balance ?? "",
                            }))
                          }
                        >
                          {t("fx.rate_use_suggested", {
                            amount: row.rate_check.suggested_balance,
                            currency: row.currency,
                          })}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                  {showSuggestionPending(row, drafts) ? (
                    <p className="muted">{t("fx.rate_suggestion_pending")}</p>
                  ) : null}
                  {row.balance === null &&
                  row.status !== "estimate_accepted" &&
                  !showDerived(row) &&
                  row.derived_reason ? (
                    <p className="muted">
                      {/* derived_from set with no balance = a calculated figure the server rejected: its reason is
                          already a full sentence about that figure, not "we could not work this out". */}
                      {row.derived_from
                        ? row.derived_reason
                        : t("fx.derived_unavailable", { reason: row.derived_reason })}{" "}
                      {t("fx.derived_unavailable_next")}
                    </p>
                  ) : null}
                </>
              ),
          },
          {
            key: "implied_rate",
            header: t("fx.col_rate"),
            render: (row) =>
              row.implied_rate === null || row.implied_rate === undefined
                ? t("fx.rate_absent")
                : t("fx.rate_display", {
                    base: data.base_currency,
                    rate: row.implied_rate,
                    currency: row.currency,
                  }),
          },
          {
            key: "status",
            header: t("fx.col_status"),
            render: (row) => <Badge tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</Badge>,
          },
          {
            key: "actions",
            header: t("fx.col_actions"),
            render: (row) =>
              row.status === "not_required" ? null : (
                <>
                  {showDerivedUnedited(row, drafts) ? (
                    <>
                      <Button
                        aria-label={t("fx.action_use_derived_for", { account: accountLabel(row) })}
                        disabled={!data.editable || saving}
                        onClick={() => {
                          setDrafts((prev) => {
                            const next = { ...prev };
                            delete next[row.account_id];
                            return next;
                          });
                          onSave(row.account_id, row.derived_balance ?? "");
                        }}
                      >
                        {t("fx.action_use_derived")}
                      </Button>{" "}
                    </>
                  ) : null}
                  {row.cleared_by_operator ? (
                    <Button
                      aria-label={t("fx.action_restore_auto_for", { account: accountLabel(row) })}
                      disabled={!data.editable || saving}
                      onClick={() => onRestoreAuto(row.account_id)}
                    >
                      {t("fx.action_restore_auto")}
                    </Button>
                  ) : null}{" "}
                  {(row.auto_fill ?? null) !== null && !editing[row.account_id] ? (
                    <Button
                      aria-label={t("fx.action_edit_for", { account: accountLabel(row) })}
                      disabled={!data.editable || saving}
                      onClick={() => setEditing((prev) => ({ ...prev, [row.account_id]: true }))}
                    >
                      {t("fx.action_edit")}
                    </Button>
                  ) : (
                    <Button
                      aria-label={t("fx.action_save_for", { account: accountLabel(row) })}
                      disabled={!data.editable || saving}
                      onClick={() => {
                        setEditing((prev) => ({ ...prev, [row.account_id]: false }));
                        onSave(row.account_id, currentValue(row, drafts));
                      }}
                    >
                      {t("fx.action_save")}
                    </Button>
                  )}{" "}
                  <Button
                    aria-label={t("fx.action_clear_for", { account: accountLabel(row) })}
                    disabled={!data.editable || saving}
                    onClick={() => {
                      // Round-2 LOW fix: a stale typed draft must not keep shadowing the server's null
                      // balance after a successful Clear.
                      setDrafts((prev) => {
                        const next = { ...prev };
                        delete next[row.account_id];
                        return next;
                      });
                      onClear(row.account_id);
                    }}
                  >
                    {t("fx.action_clear")}
                  </Button>{" "}
                  <Button
                    aria-label={t("fx.action_accept_estimate_for", {
                      base: data.base_currency,
                      account: accountLabel(row),
                    })}
                    disabled={!data.editable || saving || row.status === "entered"}
                    onClick={() => setConfirmingAccountId(row.account_id)}
                  >
                    {t("fx.action_accept_estimate", { base: data.base_currency })}
                  </Button>
                </>
              ),
          },
        ]}
      />

      {openFindings.length === 0 ? null : (
        <section aria-labelledby="scope-fx-findings">
          <h3 id="scope-fx-findings">{t("fx.findings_title")}</h3>
          {/* Backlog fix (round-4 follow-up): disabled resolve buttons after scope freeze are safe, but gave
              no hint that an open BLOCK is still resolvable -- on the Dry-run screen. Backlog fix (FX
              opening-balance LOWs, item 4): gated on `scopeFrozen`, not `!data.editable` -- a migration
              that is not editable for some OTHER reason (never reached scope sign-off, or was cancelled
              with a BLOCK still open) must not claim "Scope is signed off" when it was not. */}
          {scopeFrozen && hasOpenBlock ? (
            <p>
              {t("fx.findings_frozen_hint")}{" "}
              <Link to={pathForStage(migrationId, "dry-run")}>{t("fx.findings_frozen_hint_link")}</Link>
            </p>
          ) : null}
          <ul>
            {openFindings.map(({ account, finding }) => (
              <li key={finding.review_item_id}>
                <Badge
                  tone={
                    finding.severity === "BLOCK" ? "fail" : finding.severity === "INFO" ? "neutral" : "warn"
                  }
                >
                  {t(`severity.${finding.severity as "BLOCK" | "WARN" | "INFO"}` as "severity.BLOCK")}
                </Badge>{" "}
                {account.code} — {finding.message}{" "}
                {finding.options.map((option) => (
                  <Button
                    key={option}
                    // Backlog fix (round-4 follow-up): the visible label already names the decision
                    // (`labelForOption` below) -- the accessible name must start with the same text
                    // (WCAG 2.5.3 Label in Name), not a generic "Acknowledge the finding for …". Shares
                    // `fx.action_for_finding` with the Dry-run screen's `FxOpeningChecks`, which already
                    // does this correctly, so the two cannot drift apart on it again.
                    aria-label={t("fx.action_for_finding", {
                      option: labelForOption(option, data.base_currency, finding.options),
                      message: finding.message,
                    })}
                    // Round-4 review fix (review-fx-task2-r3.json, item 6): no resolve action once scope
                    // sign-off has frozen the scope -- this findings list is reachable read-only via the
                    // stage nav after that point, same as every OTHER action in this section already is
                    // (`!data.editable` gates Save/Clear/Accept-estimate above).
                    disabled={!data.editable || saving}
                    onClick={() => {
                      if (requiresConfirm(finding.severity)) {
                        setConfirmingFinding({
                          reviewItemId: finding.review_item_id,
                          option,
                          account: accountLabel(account),
                          message: finding.message,
                        });
                        return;
                      }
                      onAcknowledge(finding.review_item_id, option);
                    }}
                  >
                    {labelForOption(option, data.base_currency, finding.options)}
                  </Button>
                ))}
              </li>
            ))}
          </ul>
        </section>
      )}

      <ConfirmDialog
        open={confirmingAccountId !== null}
        title={t("fx.accept_estimate_confirm_title", {
          account:
            confirmingAccountId === null
              ? ""
              : accountLabel(
                  data.accounts.find((a) => a.account_id === confirmingAccountId) ?? { code: "", name: "" },
                ),
        })}
        onConfirm={() => {
          if (confirmingAccountId !== null) onAcceptEstimate(confirmingAccountId);
          setConfirmingAccountId(null);
        }}
        onCancel={() => setConfirmingAccountId(null)}
      >
        <p>{t("fx.accept_estimate_confirm_body")}</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmingFinding !== null}
        title={confirmTitleFor(confirmingFinding?.option ?? "", confirmingFinding?.account ?? "")}
        onConfirm={() => {
          if (confirmingFinding !== null)
            onAcknowledge(confirmingFinding.reviewItemId, confirmingFinding.option);
          setConfirmingFinding(null);
        }}
        onCancel={() => setConfirmingFinding(null)}
      >
        <p>{confirmingFinding?.message}</p>
        {confirmingFinding?.option === "accept_estimate" ? (
          <p>{t("fx.accept_estimate_confirm_body")}</p>
        ) : null}
      </ConfirmDialog>
    </section>
  );
}
